import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'cambio-theme';

/**
 * Theme has three states, not two.
 *
 * "System" is the default and stamps nothing on the root element, so
 * `prefers-color-scheme` decides. An explicit choice stamps `data-theme`, which
 * has to beat the media query in *both* directions - hence the duplicated dark
 * block in tokens.css.
 */
export function readStoredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  // Keeps the mobile browser chrome in step with the page.
  const resolved =
    theme === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', resolved === 'dark' ? '#120e0f' : '#f6f7f9');
}

/**
 * The theme lives outside React so the settings screen and the system appearance
 * listener always update the same source of truth.
 */
let current: Theme = readStoredTheme();
const listeners = new Set<() => void>();

export function setTheme(next: Theme): void {
  if (next === 'system') localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, next);
  current = next;
  applyTheme(next);
  for (const listener of listeners) listener();
}

export function useTheme() {
  const theme = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => current,
  );

  useEffect(() => {
    if (theme !== 'system') return;
    // Following the system means following it as it changes, not only at load.
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  return { theme, setTheme };
}

/**
 * Read a design token as a string.
 *
 * Recharts wants concrete colour strings, not CSS variables, so chart series
 * pull their colours through here and re-read them when the theme flips.
 */
export function token(name: string, fallback = ''): string {
  if (typeof window === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

/** Re-reads tokens whenever the resolved theme changes, so charts recolour. */
export function useChartTokens() {
  const read = useCallback(
    () => ({
      grid: token('--chart-grid', 'rgba(0,0,0,0.1)'),
      average: token('--chart-average', '#8f302a'),
      market: token('--chart-market', '#667085'),
      buy: token('--buy', '#cf2f25'),
      sell: token('--sell', '#14805e'),
      text: token('--text-secondary', '#666'),
      elevated: token('--bg-elevated', '#fff'),
      separator: token('--separator', '#ccc'),
    }),
    [],
  );

  const [tokens, setTokens] = useState(read);

  useEffect(() => {
    const refresh = () => setTokens(read());
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', refresh);

    // data-theme is set imperatively by applyTheme, so observe the attribute
    // rather than trying to thread the theme value through every chart.
    const observer = new MutationObserver(refresh);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    refresh();
    return () => {
      media.removeEventListener('change', refresh);
      observer.disconnect();
    };
  }, [read]);

  return tokens;
}
